import React from "react";
import { Upload, Typography, Card, theme } from "antd";
import { UploadOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { extractTextFromPDF } from "../../PDFReader/utils/extractTextFromPDF";
import { parseUserData } from "../../PDFReader/utils/parseUserData";
import type { UserData } from "../../PDFReader/types";

const { Dragger } = Upload;

interface SmartLoadProps {
  onDataExtracted: (data: UserData) => void;
  title?: string;
  description?: string;
}

const SmartLoad: React.FC<SmartLoadProps> = ({
  onDataExtracted,
  title = "Carga Inteligente",
  description = "Sube el PDF de tu Constancia de Situación Fiscal para auto-completar los datos.",
}) => {
  const { token } = theme.useToken();

  const handleFileChange = async (file: File) => {
    try {
      const text = await extractTextFromPDF(file);
      const parsed = parseUserData(text);
      onDataExtracted(parsed);
    } catch (error) {
      console.error("Error extracting PDF data:", error);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: ".pdf",
    beforeUpload: (file: File) => {
      handleFileChange(file);
      return false; // Prevents auto-upload
    },
    showUploadList: false,
  };

  return (
    <Dragger {...uploadProps} style={{ marginBottom: "32px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "12px",
          padding: "0 4px",
        }}
      >
        <CheckCircleOutlined
          style={{ color: token.colorPrimary, fontSize: "20px" }}
        />
        <Typography.Text
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "wider",
            color: token.colorTextSecondary,
          }}
        >
          {title}
        </Typography.Text>
      </div>
      <Card
        style={{
          borderRadius: "16px",
          border: `2px dashed ${token.colorPrimaryBorderHover}`,
          background: token.colorBgLayout,
          cursor: "pointer",
        }}
        bodyStyle={{ padding: "24px", textAlign: "center" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "56px",
              height: "56px",
              background: token.colorPrimaryBg,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <UploadOutlined
              style={{ fontSize: "36px", color: token.colorPrimary }}
            />
          </div>
          <Typography.Title
            level={4}
            style={{ marginBottom: "4px", color: token.colorText }}
          >
            Cargar Constancia (CSF)
          </Typography.Title>
          <Typography.Text
            style={{
              fontSize: "12px",
              color: token.colorTextSecondary,
              lineHeight: "1.5",
              maxWidth: "260px",
            }}
          >
            {description}
          </Typography.Text>
        </div>
      </Card>
    </Dragger>
  );
};

export default SmartLoad;
